import { get, post } from 'aws-amplify/api';
import useSWR from 'swr';

/**
 * Fetch the current T&C acceptance state for the authenticated user's
 * active customer. Includes the current required version, accepted
 * version, and a `requires_acceptance` boolean for gating the UI.
 */
async function getTermsStatus() {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'customer_setup/terms_status',
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export function useGetTermsStatus(shouldFetch = true) {
    const result = useSWR(
        shouldFetch ? 'TERMS_STATUS' : null,
        () => getTermsStatus(),
        {
            revalidateOnFocus: false,
        }
    );
    return result;
}

/**
 * Record an acceptance event for the user's active customer. The
 * submitted version must equal the server's CURRENT_TC_VERSION; the
 * server rejects stale versions to avoid recording a misaligned
 * consent.
 *
 * Authorization is enforced server-side: only the customer's owner
 * (User-as-Customer self, or Org Admin of Org-as-Customer) may accept.
 * Super Admins explicitly cannot accept on behalf of others.
 */
export async function acceptTerms({ version, customerID = null } = {}) {
    const requestBody = { version };
    if (customerID) requestBody.customerID = customerID;

    const payload = {
        apiName: 'CustomerSetupApi',
        path: 'customer_setup/accept_terms',
        options: { body: requestBody },
    };
    const restOperation = post(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

/**
 * Read-only audit log of acceptance events for a customer. Server-side
 * authorization: Super Admin OR customer owner.
 */
async function getTermsHistory(customerID) {
    const payload = {
        apiName: 'CustomerSetupApi',
        path: customerID
            ? `customer_setup/terms_history?customerID=${encodeURIComponent(customerID)}`
            : 'customer_setup/terms_history',
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    return await body.json();
}

export function useGetTermsHistory(customerID, shouldFetch = true) {
    const result = useSWR(
        shouldFetch ? ['TERMS_HISTORY', customerID || 'self'] : null,
        () => getTermsHistory(customerID)
    );
    return result;
}
